import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Close from "../../assets/images/close-icon.png";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import PDF from "../../assets/images/pdf.svg";
import { getCampaignView } from "../../redux/actions/campaignActions";
import { getCampaignRatingsAdmin } from "../../redux/actions/dealFlowActions";
import Loader from "../../components/Loader/Loader";
import { fmt$, fmt$Denominated } from "../../utils/format";
import FundDetailFields from "./FundDetailFields";
import FeeObligationPanel from "./FeeObligationPanel";
import ProfilePic from "../../assets/images/dummy_profile.png";
import { useReactToPrint } from "react-to-print";
import { getConfigByRoute } from "../../utils/campaignStatusConfig";
import { usePermissions } from "../../context/permissionContext";
import { canWriteQueue } from "../../utils/permissionUtils";
import { dateFormat } from "../../utils/dateUtils";
import { fetchCampaignHistory } from "../../utils/campaignViewActions";
import PageShell from "../../components/layout/PageShell";
import PageHead from "../../components/layout/PageHead";
import "../../components/common/css/campaignview.scss";
import "../../components/common/css/modal.scss";

// Action panels — conditionally rendered based on viewType
import InitialApprovalActions from "./ActionPanels/InitialApprovalActions";
import ForReviewActions from "./ActionPanels/ForReviewActions";
import ActiveActions from "./ActionPanels/ActiveActions";
import PausedActions from "./ActionPanels/PausedActions";
import ReadOnlyActions from "./ActionPanels/ReadOnlyActions";
import CompletedActions from "./ActionPanels/CompletedActions";
import CoOwnershipPanel from "./CoOwnershipPanel";
import CampaignSchedulePanel from "./CampaignSchedulePanel";

const ACTION_PANELS = {
  "initial-approval": InitialApprovalActions,
  "for-review": ForReviewActions,
  active: ActiveActions,
  // ComingSoonActions is gone: its only content was a duplicate of the launch
  // modal in ActiveActions, and both are now CampaignSchedulePanel. Mapped
  // explicitly rather than deleted so this registry still enumerates all six
  // viewTypes — the `|| ReadOnlyActions` fallback below would hide the gap.
  "coming-soon": ReadOnlyActions,
  paused: PausedActions,
  // DECISION 4: a completed raise can be re-opened, but only onto a NEW future
  // launch date. Split out of "read-only" so the other five statuses that share
  // that viewType keep offering nothing.
  completed: CompletedActions,
  "read-only": ReadOnlyActions,
};

/**
 * Unified Campaign View Page
 *
 * Renders shared campaign detail sections (personal info, corporate info,
 * documents, Q&A) and delegates status-specific actions to action panels.
 */
const CampaignViewPage = () => {
  const [loading, setLoading] = useState(false);
  const [business_plan_docs, setBuiness_Plan_Docs] = useState([]);
  const [other_docs, setOther_Docs] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [campaignData, setCampaignData] = useState(null);

  const dispatch = useDispatch();
  const campaignView = useSelector((state) => state.campaignView);
  const { campaign } = campaignView;
  const dealFlow = useSelector((state) => state.dealFlow);
  const location = useLocation();
  const id = useParams();
  const token = localStorage.getItem("accessToken");

  // Resolve config from route
  const routeResult = getConfigByRoute(location.pathname);
  const statusKey = routeResult ? routeResult[0] : null;
  const config = routeResult ? routeResult[1] : null;

  const campaignIdParam = id?.campaignId;
  const resolvedCampaignId = campaignIdParam
    ? Number(campaignIdParam)
    : campaign?.data?.[0]?.id || null;

  // Ratings data (for approval views)
  const ratingsData = resolvedCampaignId
    ? dealFlow?.ratings?.[resolvedCampaignId] || null
    : null;
  const adminId = JSON.parse(localStorage.getItem("admin"))?.data?.id || null;
  const myRating =
    ratingsData?.my_rating ||
    ratingsData?.individual_ratings?.find((r) => r?.user_id === adminId) ||
    null;

  // ---- Data fetching ----
  // Extracted so the schedule panel can refetch after a write. Deleted
  // campaigns are read-only, so they never invoke it.
  const reloadCampaign = () => dispatch(getCampaignView(token, id));
  // QA 2026-09-07 (F22): the route only asks for :View; every write control
  // below needs the queue's :Edit grant (the API mounts them behind
  // verifyAdminToken alone, so this is the only place the matrix is enforced).
  const navigate = useNavigate();
  const { permissions, permissionsMapping } = usePermissions();
  // The queue's write grant as the permission vocabulary defines it (:Edit where it
  // exists, else :Delete; undefined = the vocabulary has no write grant for this queue).
  const canEdit = canWriteQueue(config.permissionPrefix, permissions, permissionsMapping);

  useEffect(() => {
    if (statusKey === "deleted") {
      // Deleted campaigns use direct axios (Redux dispatch was commented out in original)
      fetchDeletedCampaignData();
    } else {
      reloadCampaign();
    }
    // QA 2026-09-07 (F9): re-fetch when the campaign id in the URL changes; the
    // panels read the URL id, so an in-app hash change showed campaign A while
    // acting on campaign B.
  }, [campaignIdParam, statusKey]);

  useEffect(() => {
    if (["initial-approval", "for-review"].includes(statusKey) && resolvedCampaignId) {
      dispatch(getCampaignRatingsAdmin(resolvedCampaignId));
    }
  }, [resolvedCampaignId]);

  useEffect(() => {
    if (campaignView) {
      if (campaignView?.loading !== undefined) {
        setLoading(campaignView?.loading);
      }
      const docs = campaignView?.campaign?.data?.[0]?.business_plan_docs;
      setBuiness_Plan_Docs(docs?.filter((x) => x.is_other == 0) || []);
      setOther_Docs(docs?.filter((x) => x.is_other == 1) || []);
    }
  }, [campaignView]);

  // Auto-fetch history when campaign data loads (matches original behavior)
  useEffect(() => {
    if (campaign?.status == 200 && campaign?.success == true && campaignIdParam) {
      loadHistory();
    }
  }, [campaign]);

  // Fetch history for deleted campaigns (Redux is never populated for these)
  useEffect(() => {
    if (statusKey === "deleted" && campaignData && campaignIdParam) {
      loadHistory();
    }
  }, [campaignData]);

  const fetchDeletedCampaignData = () => {
    setLoading(true);
    axios({
      method: "post",
      url: process.env.REACT_APP_API + "admin/get-campaigndetailsby-id",
      headers: { "x-access-token": token },
      data: { campaign_id: campaignIdParam, is_deleted: 1 },
    })
      .then((response) => {
        setCampaignData(response.data);
        const docs = response.data?.data?.[0]?.business_plan_docs;
        setBuiness_Plan_Docs(docs?.filter((x) => x.is_other == 0) || []);
        setOther_Docs(docs?.filter((x) => x.is_other == 1) || []);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        Swal.fire({
          title: error?.response?.data?.message || "An error occurred",
          icon: "error",
          confirmButtonText: "OK",
          showCloseButton: true,
        });
      });
  };

  const loadHistory = async () => {
    const data = await fetchCampaignHistory(campaignIdParam);
    setHistory(data?.rows || data || []);
  };

  // Use deleted data or Redux data
  const effectiveCampaign = statusKey === "deleted" ? campaignData : campaign;
  const userInfoList = effectiveCampaign?.data || [];
  const campaign_id = effectiveCampaign?.data?.[0]?.id?.toString()?.padStart(8, "0");
  const d = new Date(effectiveCampaign?.data?.[0]?.user?.dob);

  // Print support — hooks must be called before any early return
  const printRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: (campaign_id || "Campaign") + "-Detail",
  });

  if (!config) {
    return (
      <div className="single">
        <div className="singleContainer">
          <p>Unknown campaign view.</p>
        </div>
      </div>
    );
  }

  // Resolve action panel component
  const ActionPanel = ACTION_PANELS[config.viewType] || ReadOnlyActions;

  return (
    <PageShell>
      <PageHead title={config?.label ? `Campaign · ${config.label}` : "Campaign"} />
      <div className="single">
        <div className="singleContainer">
        <Loader isLoading={loading} />
        <div className="top" ref={printRef}>
          {userInfoList?.map((userInfo, i) => {
            // The campaign's own currency, resolved once per row. fmt$ pins an
            // en-CA formatter, which renders CAD as a bare '$' and USD as 'US$',
            // so passing it is what makes a USD offering visibly distinct from a
            // CAD one on this screen. No fallback to CAD: a campaign with no
            // declared currency must not be dressed up as a Canadian one on the
            // screen where it gets approved.
            const campaignCurrency = userInfo?.currency_type || undefined;
            // Same idiom as CampaignSchedulePanel.jsx:97, this repo's only other
            // fund-aware branch, rather than a second way of asking the same
            // question. Normalised because `type` is caller-supplied at create
            // and reaches the column unnormalised: a lowercase "fund" would fall
            // through a strict === and be rendered as a loan, which is precisely
            // the failure this branch exists to end.
            const isFundCampaign =
              String(userInfo?.type || "").trim().toUpperCase() === "FUND";
            return (
            <React.Fragment key={userInfo?.id || i}>
              {/* ---- Action Panel (status-specific) ---- */}
              <ActionPanel
                userInfo={userInfo}
                campaign={effectiveCampaign}
                campaignId={campaignIdParam}
                onPrint={handlePrint}
                config={config}
                statusKey={statusKey}
                setLoading={setLoading}
                ratingsData={ratingsData}
                myRating={myRating}
                dealFlow={dealFlow}
                history={history}
                canEdit={canEdit}
              />

              {/* ---- Campaign Schedule (status-independent) ----
                  Rendered here, next to the approve/reject decision, and on
                  EVERY status — including the terminal ones, where the action
                  panel is null. An absent end date being invisible is exactly
                  how the never-closes defect stayed hidden (QA §B.2). Editable
                  or read-only is decided inside, from the ROW's campaign_status
                  rather than from the route. Deliberately not passed setLoading:
                  its writes open SweetAlerts, and the F-1 overlay would swallow
                  every click. */}
              <CampaignSchedulePanel
                campaign={userInfo}
                campaignId={campaignIdParam || userInfo?.id}
                onChanged={statusKey === "deleted" ? undefined : reloadCampaign}
                canEdit={canEdit}
                onNavigate={navigate}
              />

              {/* ---- Personal Information ---- */}
              <div className="left">
                <div className="title-topSection">
                  <h1 className="title">Personal Information</h1>
                  <button onClick={() => setHistoryOpen(true)} className="editButton">
                    History
                  </button>
                </div>
                <div className="item">
                  <img
                    src={userInfo?.user?.profilePic == null ? ProfilePic : userInfo?.user?.profilePic}
                    alt=""
                    className="itemImg"
                  />
                  <div className="details profile-info">
                    <div className="detailItem">
                      <span className="itemKey">Name:</span>
                      <span className="itemValue">
                        <b>
                          {userInfo?.user?.salutations} {userInfo?.user?.firstname}{" "}
                          {userInfo?.user?.lastname}
                        </b>
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Email:</span>
                      <span className="itemValue">{userInfo?.user?.email}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Phone:</span>
                      <span className="itemValue">
                        {userInfo?.user?.phone == "null" ? "" : userInfo?.user?.phone}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Current Address:</span>
                      <span className="itemValue">{userInfo?.user?.current_address}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Floor:</span>
                      <span className="itemValue">{userInfo?.user?.address}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">City:</span>
                      <span className="itemValue">{userInfo?.user?.city}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Postal Code:</span>
                      <span className="itemValue">{userInfo?.user?.postal_code}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Country:</span>
                      <span className="itemValue">{userInfo?.user?.user_country?.country_name}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">State/Province:</span>
                      <span className="itemValue">{userInfo?.user?.user_state?.state_name}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">DOB:</span>
                      <span className="itemValue">{d?.toLocaleDateString()}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Gender:</span>
                      <span className="itemValue">{userInfo?.user?.gender}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Marital Status:</span>
                      <span className="itemValue">{userInfo?.user?.marital_status}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Employer:</span>
                      <span className="itemValue">{userInfo?.user?.employer}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Occupation:</span>
                      <span className="itemValue">{userInfo?.user?.occupation}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Tax ID (SIN / TIN / CF):</span>
                      <span className="itemValue">
                        {userInfo?.user?.sin}
                        {userInfo?.user?.tax_id_type
                          ? ` — ${userInfo.user.tax_id_type}${
                              userInfo?.user?.tax_country_code
                                ? ` (${userInfo.user.tax_country_code})`
                                : ""
                            }`
                          : ""}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ---- Corporate Information ---- */}
              <div className="left">
                <h1 className="title">Corporate Information</h1>
                <div className="item">
                  <div className="details profile-info">
                    <div className="detailItem">
                      <span className="itemKey">Campaign ID:</span>
                      <span className="itemValue">{campaign_id}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Company Name:</span>
                      <span className="itemValue">{userInfo?.corporation_legal_name}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Company Location:</span>
                      <span className="itemValue">{userInfo?.city}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Campaign Type:</span>
                      <span className="itemValue">{userInfo?.campaign_for}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Head Office Address</span>
                      <span className="itemValue">{userInfo?.registered_address}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Country</span>
                      <span className="itemValue">{userInfo?.user_country?.country_name}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">State/Province</span>
                      <span className="itemValue">{userInfo?.user_state?.state_name}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Type Of Enitity:</span>
                      <span className="itemValue">{userInfo?.entitytype}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Buisness No:</span>
                      <span className="itemValue">{userInfo?.business}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Fax</span>
                      <span className="itemValue">{userInfo?.fax}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Crax Acoount</span>
                      <span className="itemValue">{userInfo?.craax_account}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Date Of Corp</span>
                      <span className="itemValue">{userInfo?.date_of_incorp}</span>
                    </div>
                    {/* This is the screen on which an admin decides whether an
                        offering is published, and until 2026-08-01 it could not
                        show which of the two currencies it was: the Currency row
                        was the LITERAL STRING "CAD" regardless of
                        userInfo.currency_type, and every amount below carried a
                        hardcoded '$' in front of a raw, unformatted number. A
                        reviewer approving a USD campaign saw "Currency: CAD" over
                        "$2500000".

                        currency_type is already read elsewhere in this repo
                        (ForReviewActions.jsx, InitialApprovalActions.jsx) and
                        utils/format.js already exports fmt$(value, dp, currency),
                        whose en-CA formatter renders CAD as '$' and USD as 'US$'.
                        Nothing new was needed — both were simply never wired in. */}
                    <div className="detailItem">
                      <span className="itemKey">Currency</span>
                      <span className="itemValue">
                        {userInfo?.currency_type || "— not set"}
                      </span>
                    </div>
                    {/* fmt$Denominated, not fmt$, for two reasons that are the same
                        mistake in different fields.

                        ABSENCE: a FUND leaves goal_invest_amnt and
                        outside_roots_investment NULL by design — it states a target
                        raise and a maximum offering instead — and fmt$ renders NULL
                        as $0.00. A reviewer approving a fund was shown "Goal Amount:
                        $0.00", a specific wrong number rather than a blank, and
                        indistinguishable from a genuine zero.

                        DENOMINATION: campaignCurrency is
                        `userInfo?.currency_type || undefined`, and currency_type is
                        nullable, so money() fell back to CAD and rendered a bare "$".
                        A USD offering whose currency_type was never set therefore
                        displayed its amounts as what an approver reads as Canadian
                        dollars. Same confident-wrong-answer failure, one field over.
                        The ISO code is now always stated, and an unknown currency
                        says so instead of borrowing CAD's symbol. */}
                    <div className="detailItem">
                      <span className="itemKey">Goal Amount</span>
                      <span className="itemValue">
                        {fmt$Denominated(userInfo?.goal_invest_amnt, 2, campaignCurrency)}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Minimum Investment</span>
                      <span className="itemValue">
                        {fmt$Denominated(userInfo?.minimum_investment, 2, campaignCurrency)}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Other Investment</span>
                      <span className="itemValue">
                        {fmt$Denominated(userInfo?.outside_roots_investment, 2, campaignCurrency)}
                      </span>
                    </div>

                    {/* EQUITY vs LENDME vs FUND conditional fields.

                        This was a BINARY `type === "EQUITY" ? equity : lending`,
                        correct for exactly as long as there were two types. A FUND
                        fell into the else and was shown six loan fields that are all
                        NULL for it, under labels like "desired loan's duration in
                        months" - while security_type, which a fund DOES set (Units /
                        Partnership Units / Trust Units), rendered only inside the
                        equity branch and was therefore invisible on the one screen
                        where a fund gets approved. Nothing errored; it silently
                        rendered the wrong thing.

                        The discriminator matches CampaignSchedulePanel.jsx:97, this
                        repo's only other fund-aware branch, rather than inventing a
                        second idiom. */}
                    {isFundCampaign ? (
                      <FundDetailFields userInfo={userInfo} currency={campaignCurrency} />
                    ) : userInfo?.type === "EQUITY" ? (
                      <>
                        <div className="detailItem">
                          <span className="itemKey">Company Valuation</span>
                          <span className="itemValue">
                            {fmt$(userInfo?.company_valuation, 2, campaignCurrency)}
                          </span>
                        </div>
                        <div className="detailItem">
                          <span className="itemKey">No Closing Minimum</span>
                          <span className="itemValue">
                            {fmt$(userInfo?.no_closing_minimum, 2, campaignCurrency)}
                          </span>
                        </div>
                        <div className="detailItem">
                          <span className="itemKey">Price per security</span>
                          <span className="itemValue">
                            {fmt$(userInfo?.price_per_security, 2, campaignCurrency)}
                          </span>
                        </div>
                        <div className="detailItem">
                          <span className="itemKey">Security Type</span>
                          <span className="itemValue">{userInfo?.security_type}</span>
                        </div>
                        <div className="detailItem">
                          <span className="itemKey">Number of shares issued in this campaign</span>
                          <span className="itemValue">{userInfo?.issued_shares}</span>
                        </div>
                        <div className="detailItem">
                          <span className="itemKey">Total Company's shares issued</span>
                          <span className="itemValue">{userInfo?.issued_shares_company}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="detailItem">
                          <span className="itemKey">In Which Form</span>
                          <span className="itemValue">{userInfo?.loan_type}</span>
                        </div>
                        <div className="detailItem">
                          <span className="itemKey">Brief description on how you will use this funds</span>
                          <span className="itemValue">{userInfo?.fund_use}</span>
                        </div>
                        <div className="detailItem">
                          <span className="itemKey">Which Is The Purpose Of This Financing?</span>
                          <span className="itemValue">{userInfo?.purpose_of_financing}</span>
                        </div>
                        <div className="detailItem">
                          <span className="itemKey">Which return you would like to offer to the investors?</span>
                          <span className="itemValue">{userInfo?.interest_rate}</span>
                        </div>
                        <div className="detailItem">
                          <span className="itemKey">What's your desired loan's duration in months?</span>
                          <span className="itemValue">{userInfo?.loan_duration_in_month}</span>
                        </div>
                        <div className="detailItem">
                          <span className="itemKey">Can you offer any guarantees to the investor?</span>
                          <span className="itemValue">{userInfo?.guarantee_type}</span>
                        </div>
                      </>
                    )}

                    {/* The onboarding fee, on the screen where the campaign is
                        approved. It gates nothing - whether approval should be
                        blocked on a paid fee is a product decision - but Schedule
                        "A" cl.1 gives RFI a right to withhold work until the fee
                        is received, and that right was unexercisable while there
                        was no way to see or record receipt. */}
                    <FeeObligationPanel campaignId={userInfo?.id} />

                    <div className="detailItem">
                      <span className="itemKey">Year Of Corporation</span>
                      <span className="itemValue">{userInfo?.how_many_yearsin_business}</span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Feature</span>
                      {Array.isArray(userInfo?.features) ? (
                        userInfo?.features.map((feature, index) => (
                          <li className="itemValue" key={index}>
                            {feature?.feature_title}
                          </li>
                        ))
                      ) : (
                        <h4>No Data Found</h4>
                      )}
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Nature Of Buisness</span>
                      {Array.isArray(userInfo?.nature_of_businesses) ? (
                        userInfo?.nature_of_businesses.map((cat, index) => (
                          <li className="itemValue" key={index}>
                            {cat?.business_category?.category_name}
                          </li>
                        ))
                      ) : (
                        <h4>No Data Found</h4>
                      )}
                    </div>

                    {/* Documents */}
                    <div className="buisnessPlan">
                      <span className="itemKey">Buisness Plan Docs</span>
                      <div className="itemValue">
                        {business_plan_docs?.length > 0 ? (
                          business_plan_docs?.map((pdf, i) => (
                            <a href={pdf.document_name} target="_blank" className="docLink" key={i}>
                              {pdf?.document_name !== null && <img src={PDF} className="docImage" alt="" />}
                              {pdf?.document_name !== null && <p className="docName">doc {i + 1}</p>}
                            </a>
                          ))
                        ) : (
                          <h6>No Data Found</h6>
                        )}
                      </div>
                    </div>

                    <div className="buisnessPlan">
                      <span className="itemKey">Other Docs</span>
                      <div className="itemValue">
                        {other_docs?.length > 0 ? (
                          other_docs?.map((pdf, i) => (
                            <a href={pdf.document_name} target="_blank" className="docLink" key={i}>
                              {pdf?.document_name !== null && <img src={PDF} className="docImage" alt="" />}
                              {pdf?.document_name !== null && <p className="docName">doc {i + 1}</p>}
                            </a>
                          ))
                        ) : (
                          <h6>No Data Found</h6>
                        )}
                      </div>
                    </div>

                    <div className="buisnessPlan">
                      <span className="itemKey">Campaign Banner</span>
                      <div className="itemValue">
                        {userInfo?.banner_img != null ? (
                          <img
                            src={userInfo?.banner_img == null ? ProfilePic : userInfo?.banner_img}
                            alt=""
                            className="campaignBanner-img"
                          />
                        ) : (
                          "No Data Found"
                        )}
                      </div>
                    </div>

                    <div className="detailItem">
                      <span className="itemKey">Product:</span>
                      <span className="itemValue">
                        {userInfo?.product?.product_name || userInfo?.product_type || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ---- Campaign Q&A ---- */}
              <div className="left">
                <h1 className="title">Campaign related Questions/Answers</h1>
                <div className="item">
                  <div className="details profile-info">
                    {userInfo?.campaign_que_ans?.length > 0 ? (
                      userInfo?.campaign_que_ans?.map((que) => (
                        <div className="detailItem companyInfo" key={que.q_id}>
                          <span className="itemKey">{que.question}:</span>
                          <span className="itemValue">{que.answer}</span>
                        </div>
                      ))
                    ) : (
                      <h5>No Data Found</h5>
                    )}
                  </div>
                </div>
              </div>
              {/* ---- Co-ownership (AD only) ----
                  Until this shipped there was NO admin surface for
                  campaign_participants / campaign_invitations anywhere in the
                  console: staff could not see who co-owned a campaign, and
                  unwinding a bad co-owner needed a hand-rolled API call.
                  Status-independent by design, which is why it is a sibling
                  section here rather than one of the six status-keyed
                  ActionPanels. */}
              <CoOwnershipPanel
                campaignId={userInfo?.id}
                campaignOwnerId={userInfo?.user_id}
              />
            </React.Fragment>
            );
          })}

          {/* ---- History Modal ---- */}
          <Modal
            open={historyOpen}
            onClose={() => setHistoryOpen(false)}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box className="modal-popup">
              <div className="modal-top">
                <Typography id="modal-modal-title" variant="h6" component="h2">
                  Campaign Activity
                </Typography>
                <Button className="closeBtn" onClick={() => setHistoryOpen(false)}>
                  <img src={Close} alt="close-icon" />
                </Button>
              </div>
              <Typography id="modal-modal-description" sx={{ mt: 1 }}>
                <div className="new">
                  <div className="newContainer">
                    <div className="bottom" style={{ boxShadow: "none", padding: "0" }}>
                      <div className="right">
                        <form className="accredited-investorForm">
                          {history.length > 0 ? (
                            <div className="history-infoWrap">
                              {/* QA 2026-09-07 (F31): every event, not history[0] only */}
                              {history.map((h, hi) => (
                                <p className="campCreated-name" key={h?.id || hi}>
                                  {h?.user?.firstname}&nbsp;
                                  {h?.user?.lastname}&nbsp; Updated The Status to &nbsp;
                                  {h?.action}&nbsp; on &nbsp;
                                  {dateFormat(h?.timestamp)}&nbsp; For &nbsp;
                                  {h?.campaign_detail?.corporation_legal_name}&nbsp; Campaign.
                                </p>
                              ))}
                            </div>
                          ) : (
                            <h3>No Data Found</h3>
                          )}
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </Typography>
            </Box>
          </Modal>
        </div>
        </div>
      </div>
    </PageShell>
  );
};

export default CampaignViewPage;
